import React, { useState, useRef } from "react";
import { useScrollAnimation } from "../hooks/useScrollAnimation";

const galleryImages = [
  "/assets/images/gallery/1.jpg",
  "/assets/images/gallery/2.jpg",
  "/assets/images/gallery/3.jpg",
  "/assets/images/gallery/4.jpg",
  "/assets/images/gallery/5.jpg",
  "/assets/images/gallery/6.jpg",
  "/assets/images/gallery/7.jpg",
  "/assets/images/gallery/8.jpg",
  "/assets/images/gallery/9.jpg",
  "/assets/images/gallery/12.jpg",
  "/assets/images/gallery/13.jpg",
  "/assets/images/gallery/14.jpg",
  "/assets/images/gallery/15.jpg",
  "/assets/images/gallery/16.jpg",
  "/assets/images/gallery/17.jpg",
  "/assets/images/gallery/18.jpg",
];

function Gallery() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const dragStartX = useRef(0);
  const dragOffset = useRef(0);

  const [titleRef, titleVisible] = useScrollAnimation();
  const [headingRef, headingVisible] = useScrollAnimation();
  const [arrowsRef, arrowsVisible] = useScrollAnimation();
  const [sliderRef, sliderVisible] = useScrollAnimation();

  const nextSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev + 1) % galleryImages.length);
    setTimeout(() => {
      setIsTransitioning(false);
    }, 400);
  };

  const prevSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide(
      (prev) => (prev - 1 + galleryImages.length) % galleryImages.length
    );
    setTimeout(() => {
      setIsTransitioning(false);
    }, 400);
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    dragStartX.current = e.pageX || e.touches?.[0]?.pageX;
    dragOffset.current = 0;
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const currentX = e.pageX || e.touches?.[0]?.pageX;
    dragOffset.current = currentX - dragStartX.current;
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    setIsDragging(false);

    if (dragOffset.current > 50) {
      prevSlide();
    } else if (dragOffset.current < -50) {
      nextSlide();
    }
    dragOffset.current = 0;
  };

  const getImagePosition = (index) => {
    const diff = index - currentSlide;
    const total = galleryImages.length;
    const normalizedDiff = ((diff % total) + total) % total;

    // Map positions: -2 (small-left), -1 (medium-left), 0 (main), 1 (medium-right), 2 (small-right)
    if (normalizedDiff === 0) return "main-img";
    if (normalizedDiff === 1) return "medium-img medium-right";
    if (normalizedDiff === 2) return "small-img small-right";
    if (normalizedDiff === total - 1) return "medium-img medium-left";
    if (normalizedDiff === total - 2) return "small-img small-left";
    return "hidden-img";
  };

  const getVisibleImages = () => {
    // Render all images for smooth transitions
    return galleryImages.map((src, index) => ({
      index,
      src,
      className: getImagePosition(index),
    }));
  };

  return (
    <div className="gallery-wrapper">
      <div className="gallery container">
        <div
          ref={titleRef}
          className={`gallery-title flex ${titleVisible ? "animate-in" : ""}`}
        >
          <div className="circle"></div>
          <h4 className="clr-green fs-700">Our Gallery</h4>
        </div>
        <h2
          ref={headingRef}
          className={`fs-900 ${headingVisible ? "animate-in" : ""}`}
        >
          Where Every Image Tells a Story
        </h2>
        <div
          ref={arrowsRef}
          className={`flex arrows ${arrowsVisible ? "animate-in" : ""}`}
        >
          <img
            src="/assets/icons/arrowLeftSimple.svg"
            alt="Arrow Left"
            onClick={prevSlide}
            style={{ cursor: "pointer" }}
          />
          <img
            src="/assets/icons/arrowRightSimple.svg"
            alt="Arrow Right"
            onClick={nextSlide}
            style={{ cursor: "pointer" }}
          />
        </div>
        <div
          ref={sliderRef}
          className={`gallery-slider flex ${sliderVisible ? "animate-in" : ""}`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleMouseDown}
          onTouchMove={handleMouseMove}
          onTouchEnd={handleMouseUp}
          style={{ cursor: isDragging ? "grabbing" : "grab" }}
        >
          {getVisibleImages().map((image, idx) => (
            <img
              key={`${image.index}-${idx}`}
              src={image.src}
              alt={`Gallery ${image.index + 1}`}
              className={image.className}
              draggable="false"
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default Gallery;
